import {useCallback, useEffect, useMemo, useState} from 'react';

import debounce from 'lodash.debounce';
import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {getMySavedSearches} from 'src/openapi/admin/apis/SearchSavedSearchApi';
import {AdvancedSearchType} from 'src/openapi/admin/models/AdvancedSearchType';
import {DateRangeFilter} from 'src/openapi/admin/models/DateRangeFilter';
import {SavedSearchRequestSortField} from 'src/openapi/admin/models/SavedSearchRequest';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {useAppDispatch} from 'src/store/hooks';

import {DEFAULT_PAGE_SIZE} from '../constants';


type Props = {
    userId: number,
};

export const useGetSavedSearches = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<string>('');

    const [searchType, setSearchType] = useState<AdvancedSearchType | ''>('');

    // Sort and filter state
    const [sortField, setSortField] = useState<SavedSearchRequestSortField>(SavedSearchRequestSortField.NAME);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);
    const [typeFilter, setTypeFilter] = useState<AdvancedSearchType[]>([]);
    const [createDateFilter, setCreateDateFilter] = useState<DateRangeFilter | undefined>(undefined);
    const [lastRunDateFilter, setLastRunDateFilter] = useState<DateRangeFilter | undefined>(undefined);
    const [lastSharedDateFilter, setLastSharedDateFilter] = useState<DateRangeFilter | undefined>(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetQuery = useCallback(debounce((query: string) => {
        setDebouncedQuery(query);
    }, 1000), []);

    useEffect(() => {
        debouncedSetQuery(searchQuery.trim());
    }, [searchQuery, debouncedSetQuery]);

    const isEmptySearchCriteria = useMemo(() => {
        const isQueryEmpty = !searchQuery || searchQuery.trim().length === 0;
        const isSearchTypeEmpty = !searchType;
        const isTypeFilterEmpty = !typeFilter || typeFilter.length === 0;
        const isCreateDateEmpty = !createDateFilter;
        const isLastRunDateEmpty = !lastRunDateFilter;
        const isLastSharedDateEmpty = !lastSharedDateFilter;

        return (
            isQueryEmpty &&
            isSearchTypeEmpty &&
            isTypeFilterEmpty &&
            isCreateDateEmpty &&
            isLastRunDateEmpty &&
            isLastSharedDateEmpty
        );
    }, [searchQuery, searchType, typeFilter, createDateFilter, lastRunDateFilter, lastSharedDateFilter]);

    const getDispatchAction = useCallback(() => {
        // compute searchTypes without nested ternary for lint rules
        let searchTypes: AdvancedSearchType[] | undefined;
        if (typeFilter.length > 0) {
            searchTypes = typeFilter;
        } else if (searchType) {
            searchTypes = [searchType];
        } else {
            searchTypes = undefined;
        }

        return apiDispatch(
            getMySavedSearches({userId,
                savedSearchRequest: {
                    pagination: {currentPage, pageSize},
                    sortDirection,
                    sortField,
                    nameQuery: debouncedQuery || '',
                    searchTypes,
                    createDateFilter,
                    lastRunDateFilter,
                    lastSharedDateFilter,
                }}),
        );
    }, [userId,
        currentPage,
        pageSize,
        apiDispatch,
        debouncedQuery,
        searchType,
        sortDirection,
        sortField,
        typeFilter,
        createDateFilter,
        lastRunDateFilter,
        lastSharedDateFilter]);

    const {data, isLoading, loadData, loadingError} = useReadOnlyDataHook({
        initValue: {
            searches: [],
            totalCount: 0,
        },
        getDispatchAction,
        shouldLoadData: false,
    });

    useEffect(() => {
        loadData();
    }, [loadData, currentPage,
        pageSize,
        debouncedQuery,
        searchType,
        sortDirection,
        sortField,
        typeFilter,
        createDateFilter,
        lastRunDateFilter,
        lastSharedDateFilter]);

    return {
        data,
        isLoading,
        load: loadData,
        loadingError,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        searchQuery,
        setSearchQuery,
        searchType,
        setSearchType,
        // Sort and filter controls
        sortField,
        setSortField,
        sortDirection,
        setSortDirection,
        typeFilter,
        setTypeFilter,
        createDateFilter,
        setCreateDateFilter,
        lastRunDateFilter,
        setLastRunDateFilter,
        lastSharedDateFilter,
        setLastSharedDateFilter,
        isEmptySearchCriteria,
        totalCount: data?.totalCount || 0,
    };
};
